// src/pages/ProfilePage.jsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n/i18n';
import { userApi } from '../api/userApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
    EyeIcon, EyeSlashIcon, CheckCircleIcon,
    XCircleIcon, PencilSquareIcon, GlobeAltIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/formatters';

const PASSWORD_RULES = [
    { id: 'length', label: 'Minimo 8 caratteri', test: p => p.length >= 8 },
    { id: 'upper', label: 'Una lettera maiuscola', test: p => /[A-Z]/.test(p) },
    { id: 'lower', label: 'Una lettera minuscola', test: p => /[a-z]/.test(p) },
    { id: 'number', label: 'Un numero', test: p => /[0-9]/.test(p) },
];

const ROLE_LABELS = {
    SUPER_ADMIN: 'Super Admin', SITE_MANAGER: 'Capo Cantiere',
    ENGINEER: 'Ingegnere', ACCOUNTANT: 'Contabile',
    FIELD_SUPERVISOR: 'Supervisore', VIEWER: 'Visualizzatore',
};

const ROLE_COLORS = {
    SUPER_ADMIN: 'bg-purple-100 text-purple-700',
    SITE_MANAGER: 'bg-blue-100 text-blue-700',
    ENGINEER: 'bg-indigo-100 text-indigo-700',
    ACCOUNTANT: 'bg-emerald-100 text-emerald-700',
    FIELD_SUPERVISOR: 'bg-yellow-100 text-yellow-700',
    VIEWER: 'bg-gray-100 text-gray-600',
};

const LANGUAGES = [
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
];

const ProfilePage = () => {
    const { user, refreshOrganization } = useAuth();
    const { t, i18n } = useTranslation();

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        language: user?.language || 'it',
    });

    const [passwordData, setPasswordData] = useState({
        oldPassword: '', newPassword: '', confirmPassword: '',
    });

    const [formErrors, setFormErrors] = useState({});
    const [pwErrors, setPwErrors] = useState({});

    if (!user) return <LoadingSpinner />;

    const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
    const passwordStrength = PASSWORD_RULES.filter(r => r.test(passwordData.newPassword)).length;
    const strengthColors = ['bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-emerald-500'];
    const strengthLabels = ['Molto debole', 'Debole', 'Buona', 'Forte'];

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!formData.firstName.trim()) errors.firstName = 'Nome richiesto';
        if (!formData.lastName.trim()) errors.lastName = 'Cognome richiesto';
        if (!formData.email.trim()) errors.email = 'Email richiesta';
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setLoading(true);
        try {
            await userApi.update(user.id, formData);
            if (formData.language !== i18n.language) changeLanguage(formData.language);
            toast.success('Profilo aggiornato!');
            setIsEditing(false);
            await refreshOrganization();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Errore aggiornamento');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!passwordData.oldPassword) errors.oldPassword = 'Password attuale richiesta';
        if (!PASSWORD_RULES.every(r => r.test(passwordData.newPassword))) errors.newPassword = 'La password non soddisfa i requisiti';
        if (passwordData.newPassword !== passwordData.confirmPassword) errors.confirmPassword = 'Le password non coincidono';
        setPwErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setLoading(true);
        try {
            await userApi.changePassword({ oldPassword: passwordData.oldPassword, newPassword: passwordData.newPassword });
            toast.success('Password aggiornata!');
            setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Errore cambio password');
        } finally {
            setLoading(false);
        }
    };

    const ic = (err, extra = '') =>
        `w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${err ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'} ${extra}`;

    return (
        <div className="max-w-3xl mx-auto space-y-6">

            {/* Header profilo */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-5">
                    <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-3xl font-bold border-2 border-white/30">
                        {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-bold">{user.firstName} {user.lastName}</h1>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[user.role]}`}>
                                {ROLE_LABELS[user.role] || user.role}
                            </span>
                        </div>
                        <p className="text-blue-200 mt-1 truncate">{user.email}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-blue-200 flex-wrap">
                            <span>📅 Membro dal {formatDate(user.createdAt)}</span>
                            {user.lastLogin && <span>🕐 Ultimo accesso {formatDate(user.lastLogin)}</span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Lingua */}
            <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50 flex items-center gap-2">
                    <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                    <h2 className="font-semibold text-gray-900">Lingua dell'interfaccia</h2>
                </div>
                <div className="p-6">
                    <p className="text-sm text-gray-500 mb-4">Seleziona la lingua. Viene applicata immediatamente.</p>
                    <div className="flex flex-wrap gap-3">
                        {LANGUAGES.map(lang => (
                            <button key={lang.code}
                                onClick={() => { changeLanguage(lang.code); setFormData(f => ({ ...f, language: lang.code })); }}
                                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition ${i18n.language === lang.code ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-100 text-gray-600 hover:border-gray-200 hover:bg-gray-50'
                                    }`}>
                                <span className="text-xl">{lang.flag}</span>
                                {lang.label}
                                {i18n.language === lang.code && <CheckCircleIcon className="w-4 h-4 text-blue-500" />}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Informazioni personali */}
            <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-900">Informazioni Personali</h2>
                    {!isEditing && (
                        <button onClick={() => setIsEditing(true)}
                            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                            <PencilSquareIcon className="w-4 h-4" />
                            {t('common.edit')}
                        </button>
                    )}
                </div>
                <div className="p-6">
                    {!isEditing ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {[
                                { label: 'Nome', value: user.firstName },
                                { label: 'Cognome', value: user.lastName },
                                { label: 'Email', value: user.email },
                                { label: 'Ruolo', value: ROLE_LABELS[user.role] || user.role },
                                { label: 'Lingua', value: LANGUAGES.find(l => l.code === user.language)?.label || '—' },
                                { label: 'Valuta', value: user.currency || '—' },
                            ].map(({ label, value }) => (
                                <div key={label}>
                                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
                                    <p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <form onSubmit={handleProfileUpdate} className="space-y-4" noValidate>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome *</label>
                                    <input type="text" value={formData.firstName}
                                        onChange={e => { setFormData({ ...formData, firstName: e.target.value }); setFormErrors({ ...formErrors, firstName: '' }); }}
                                        className={ic(formErrors.firstName)} />
                                    {formErrors.firstName && <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Cognome *</label>
                                    <input type="text" value={formData.lastName}
                                        onChange={e => { setFormData({ ...formData, lastName: e.target.value }); setFormErrors({ ...formErrors, lastName: '' }); }}
                                        className={ic(formErrors.lastName)} />
                                    {formErrors.lastName && <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                                <input type="email" value={formData.email}
                                    onChange={e => { setFormData({ ...formData, email: e.target.value }); setFormErrors({ ...formErrors, email: '' }); }}
                                    className={ic(formErrors.email)} />
                                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                            </div>
                            <div className="flex gap-3 justify-end pt-2">
                                <button type="button" onClick={() => { setIsEditing(false); setFormErrors({}); }}
                                    className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium">
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" disabled={loading}
                                    className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium disabled:opacity-50">
                                    {loading ? t('common.saving') : t('common.save')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* Cambio password */}
            <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50">
                    <h2 className="font-semibold text-gray-900">Sicurezza — Cambia Password</h2>
                </div>
                <div className="p-6">
                    <form onSubmit={handlePasswordChange} className="space-y-4" noValidate>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password attuale *</label>
                            <div className="relative">
                                <input type={showOld ? 'text' : 'password'} value={passwordData.oldPassword}
                                    onChange={e => { setPasswordData({ ...passwordData, oldPassword: e.target.value }); setPwErrors({ ...pwErrors, oldPassword: '' }); }}
                                    className={ic(pwErrors.oldPassword, 'pr-10')} placeholder="••••••••" />
                                <button type="button" onClick={() => setShowOld(!showOld)} tabIndex={-1}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showOld ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                </button>
                            </div>
                            {pwErrors.oldPassword && <p className="text-red-500 text-xs mt-1">{pwErrors.oldPassword}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nuova password *</label>
                            <div className="relative">
                                <input type={showNew ? 'text' : 'password'} value={passwordData.newPassword}
                                    onChange={e => { setPasswordData({ ...passwordData, newPassword: e.target.value }); setPwErrors({ ...pwErrors, newPassword: '' }); }}
                                    className={ic(pwErrors.newPassword, 'pr-10')} placeholder="••••••••" />
                                <button type="button" onClick={() => setShowNew(!showNew)} tabIndex={-1}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showNew ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                </button>
                            </div>
                            {passwordData.newPassword && (
                                <>
                                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div className={`h-full transition-all ${strengthColors[passwordStrength - 1] || 'bg-gray-200'}`}
                                            style={{ width: `${(passwordStrength / 4) * 100}%` }} />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">{strengthLabels[passwordStrength - 1] || 'Inserisci una password'}</p>
                                    <div className="mt-2 grid grid-cols-2 gap-1">
                                        {PASSWORD_RULES.map(rule => {
                                            const ok = rule.test(passwordData.newPassword);
                                            return (
                                                <div key={rule.id} className="flex items-center gap-1.5">
                                                    {ok ? <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                                        : <XCircleIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />}
                                                    <span className={`text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>{rule.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            {pwErrors.newPassword && <p className="text-red-500 text-xs mt-1">{pwErrors.newPassword}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Conferma nuova password *</label>
                            <div className="relative">
                                <input type={showConfirm ? 'text' : 'password'} value={passwordData.confirmPassword}
                                    onChange={e => { setPasswordData({ ...passwordData, confirmPassword: e.target.value }); setPwErrors({ ...pwErrors, confirmPassword: '' }); }}
                                    className={`${ic(pwErrors.confirmPassword, 'pr-10')} ${passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword ? '!border-emerald-400 !bg-emerald-50' : ''
                                        }`} placeholder="••••••••" />
                                <button type="button" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showConfirm ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                </button>
                            </div>
                            {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && (
                                <p className="text-emerald-600 text-xs mt-1 flex items-center gap-1">
                                    <CheckCircleIcon className="w-3.5 h-3.5" /> Le password coincidono
                                </p>
                            )}
                            {pwErrors.confirmPassword && <p className="text-red-500 text-xs mt-1">{pwErrors.confirmPassword}</p>}
                        </div>

                        <button type="submit"
                            disabled={loading || !PASSWORD_RULES.every(r => r.test(passwordData.newPassword))}
                            className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition text-sm font-medium disabled:opacity-40">
                            {loading ? 'Aggiornamento...' : '🔒 Aggiorna password'}
                        </button>
                    </form>
                </div>
            </div>

            {/* Danger zone */}
            <div className="bg-white rounded-2xl border border-red-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-red-100 bg-red-50">
                    <h2 className="font-semibold text-red-900">⚠️ Zona Pericolosa</h2>
                </div>
                <div className="p-6 flex items-center justify-between gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-900">Elimina account</p>
                        <p className="text-xs text-gray-400 mt-0.5">Questa azione è irreversibile.</p>
                    </div>
                    <button onClick={() => toast.error("Contatta il supporto per eliminare l'account")}
                        className="flex-shrink-0 px-4 py-2 border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition text-sm font-medium">
                        Elimina account
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;